<template>
  <div class="contenedor-editar-oficio-autorizacion">
    <v-form
      ref="editarOficioForm"
      v-model="formularioValido">
      <v-card class="rounded-lg">
        <!-- Header -->
        <v-card-title class="d-flex align-center py-4">
          <v-avatar color="warning" size="40" class="mr-3">
            <v-icon dark>mdi-file-document-edit</v-icon>
          </v-avatar>
          <div>
            <span class="text-h6">Editar Oficio de Autorización</span>
            <div class="text-caption grey--text">
              {{ oficioAutorizacionEditar.numero }}
            </div>
          </div>
          <v-spacer></v-spacer>
          <v-btn icon @click="cerrar">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="pt-6">
          <!-- Sección: Información básica -->
          <div class="section-title mb-4">
            <v-icon small color="primary" class="mr-2">mdi-information</v-icon>
            <span class="text-subtitle-2 font-weight-medium">
              Información Básica
            </span>
          </div>

          <v-row>
            <v-col cols="12" sm="6">
              <custom-select
                v-model="oficio.tipo"
                label="Tipo de oficio *"
                :items="itemsTipos"
                item-text="tipo"
                item-value="tipo"
                :rules="[mixReglas.requerido]"
                :prepend-inner-icon="getTipoIcon"
                required>
                <template v-slot:item="{ item }">
                  <v-icon
                    small
                    class="mr-2"
                    :color="item.tipo === 'AUTORIZACIÓN' ? 'success' : 'error'">
                    {{ item.tipo === 'AUTORIZACIÓN'
                      ? 'mdi-check-circle' : 'mdi-close-circle' }}
                  </v-icon>
                  {{ item.tipo }}
                </template>
              </custom-select>
            </v-col>
            <v-col cols="12" sm="6">
              <custom-text-field
                v-model="oficio.numero"
                label="Número de oficio *"
                :rules="[mixReglas.requerido]"
                prepend-inner-icon="mdi-identifier"
                required>
              </custom-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" sm="6">
              <custom-date-picker
                v-model="oficio.fecha"
                cols="12"
                label="Fecha del oficio *"
                :rules="[mixReglas.requerido]"
                prepend-inner-icon="mdi-calendar"
                required>
              </custom-date-picker>
            </v-col>
            <v-col cols="12" sm="6">
              <custom-text-field
                v-model.number="oficio.importe"
                label="Importe *"
                es-dinero
                prefix="$"
                :rules="[mixReglas.requerido, mixReglas.mayorQueCeroDinero]"
                required>
              </custom-text-field>
            </v-col>
          </v-row>

          <!-- Sección: Fechas de seguimiento -->
          <div class="section-title mb-4 mt-6">
            <v-icon small color="primary" class="mr-2">mdi-calendar-clock</v-icon>
            <span class="text-subtitle-2 font-weight-medium">
              Fechas de Seguimiento
            </span>
            <span class="text-caption grey--text ml-2">(Opcional)</span>
          </div>

          <v-row>
            <v-col cols="12" sm="6">
              <custom-date-picker
                v-model="oficio.fechaRecibido"
                cols="12"
                label="Fecha recibido"
                prepend-inner-icon="mdi-inbox-arrow-down"
                hint="Fecha en que se recibió el oficio">
              </custom-date-picker>
            </v-col>
            <v-col cols="12" sm="6">
              <custom-date-picker
                v-model="oficio.fechaVencimiento"
                cols="12"
                label="Fecha vencimiento"
                prepend-inner-icon="mdi-clock-alert"
                hint="Fecha límite para la autorización">
              </custom-date-picker>
            </v-col>
          </v-row>

          <!-- Sección: Documento anexo -->
          <div class="section-title mb-4 mt-6">
            <v-icon small color="primary" class="mr-2">mdi-paperclip</v-icon>
            <span class="text-subtitle-2 font-weight-medium">
              Documento Anexo
            </span>
            <span class="text-caption grey--text ml-2">(Opcional)</span>
          </div>

          <v-card outlined class="pa-4 rounded-lg upload-area">
            <v-file-input
              v-show="false"
              ref="anexoDocumento"
              name="anexoEditar"
              id="anexoEditar"
              accept=".pdf"
              @change="seleccion">
            </v-file-input>

            <div
              v-if="!nombreArchivoSeleccionado && !tieneArchivoExistente"
              class="text-center upload-placeholder"
              @click="seleccionarArchivo">
              <v-icon size="48" color="grey lighten-1">
                mdi-file-pdf-box
              </v-icon>
              <div class="text-body-2 grey--text mt-2">
                Haz clic para seleccionar un archivo PDF
              </div>
              <div class="text-caption grey--text">
                Tamaño máximo: 10MB
              </div>
            </div>

            <div v-else class="d-flex align-center">
              <v-avatar color="error" size="40" class="mr-3">
                <v-icon dark small>mdi-file-pdf-box</v-icon>
              </v-avatar>
              <div class="flex-grow-1">
                <div class="text-body-2 font-weight-medium">
                  {{ nombreArchivoSeleccionado || 'PDF Oficio (existente)' }}
                </div>
                <div class="text-caption grey--text">
                  {{ rutaArchivo ? 'Nuevo archivo seleccionado' : 'Archivo actual' }}
                </div>
              </div>
              <v-btn
                icon
                small
                color="error"
                @click="eliminarArchivo"
                v-if="rutaArchivo">
                <v-icon small>mdi-close</v-icon>
              </v-btn>
              <v-btn
                icon
                small
                color="primary"
                class="ml-1"
                @click="seleccionarArchivo">
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
            </div>
          </v-card>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="cerrar"
            class="mr-2">
            Cancelar
          </v-btn>
          <v-btn
            color="warning"
            @click="guardar"
            :disabled="!formularioValido || enviandoFormulario"
            :loading="enviandoFormulario">
            <v-icon left small>mdi-content-save</v-icon>
            Actualizar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </div>
</template>

<script>
import CustomTextField from '@/components/inputs/TextField.vue';
import CustomDatePicker from '@/components/inputs/DatePicker.vue';
import CustomSelect from '@/components/inputs/Select.vue';
import mixOficiosAutorizacion from '@/components/administracion/programacion/oficiosautorizacion/mixOficiosAutorizacion';

export default {
  name: 'EditarOficioAutorizacion',
  props: {
    oficioAutorizacionEditar: {
      type: Object,
      required: true,
    },
  },
  mixins: [
    mixOficiosAutorizacion,
  ],
  components: {
    CustomDatePicker,
    CustomSelect,
    CustomTextField,
  },
  data() {
    return {
      oficio: {
        tipo: this.oficioAutorizacionEditar.tipo,
        numero: this.oficioAutorizacionEditar.numero,
        fecha: this.oficioAutorizacionEditar.fecha,
        importe: this.oficioAutorizacionEditar.importe,
        fileData: this.oficioAutorizacionEditar.fileData,
        fechaRecibido: this.oficioAutorizacionEditar.fechaRecibido,
        fechaVencimiento: this.oficioAutorizacionEditar.fechaVencimiento,
      },
      itemsTipos: [
        { tipo: 'AUTORIZACIÓN', valor: 1 },
        { tipo: 'CANCELACIÓN', valor: 2 },
      ],
      formularioValido: false,
      enviandoFormulario: false,
      rutaArchivo: null,
      nombreArchivoSeleccionado: null,
      archivoAnexo: null,
    };
  },
  mounted() {
    this.$nextTick(() => {
      if (this.$refs.editarOficioForm) {
        this.$refs.editarOficioForm.validate();
      }
    });
  },
  computed: {
    getTipoIcon() {
      if (this.oficio.tipo === 'AUTORIZACIÓN') return 'mdi-check-circle';
      if (this.oficio.tipo === 'CANCELACIÓN') return 'mdi-close-circle';
      return 'mdi-help-circle-outline';
    },
    tieneArchivoExistente() {
      return this.oficioAutorizacionEditar.fileData !== null
        && this.oficioAutorizacionEditar.fileData !== '';
    },
  },
  methods: {
    cerrar() {
      this.limpiarFormulario();
      this.$emit('cerrar');
    },
    limpiarFormulario() {
      this.$refs.editarOficioForm.reset();
    },
    seleccion() {
      const fileInput = document.getElementById('anexoEditar');
      if (fileInput.files.length === 1) {
        const [archivo] = fileInput.files;
        this.rutaArchivo = URL.createObjectURL(archivo);
        this.nombreArchivoSeleccionado = archivo.name;
        this.archivoAnexo = archivo;
      }
    },
    seleccionarArchivo() {
      document.getElementById('anexoEditar').click();
    },
    eliminarArchivo() {
      this.rutaArchivo = null;
      this.nombreArchivoSeleccionado = null;
      this.archivoAnexo = null;
      const fileInput = document.getElementById('anexoEditar');
      if (fileInput) fileInput.value = '';
    },
    setDatosArchivo() {
      return {
        nombre: 'oficioautorizacion',
        path: 'Programacion/',
        archivo: this.archivoAnexo,
      };
    },
    async guardar() {
      this.enviandoFormulario = true;
      const datosArchivo = this.setDatosArchivo();
      if (this.rutaArchivo !== null) {
        const archivo = await this.mixSubirArchivo(datosArchivo);
        if (archivo.status === 'ok') {
          this.oficio.fileData = JSON.stringify(archivo.data);
        }
      }
      await this.mixEditarOficio();
    },
  },
};
</script>

<style lang="scss" scoped>
.section-title {
  display: flex;
  align-items: center;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  padding-bottom: 8px;
}

.upload-area {
  border-style: dashed !important;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--v-primary-base) !important;
    background-color: rgba(var(--v-primary-base), 0.02);
  }
}

.upload-placeholder {
  cursor: pointer;
  padding: 24px;
}
</style>
